import axios from 'axios';
import type {
  AuditLogListItem,
  AuditLogDetailResponse,
  AuditLogSearchCondition,
  ActionCatalogItem,
  ResourceCatalogItem,
  PageResponse,
  PurgeResponse,
} from '../types/auditLog';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    return Promise.reject({ code, message, traceId });
  }
);

function buildSearchParams(params: AuditLogSearchCondition): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.action) sp.set('action', params.action);
  if (params.resourceType) sp.set('resourceType', params.resourceType);
  if (params.resourceId) sp.set('resourceId', params.resourceId);
  if (params.actor) sp.set('actor', params.actor);
  if (params.result) sp.set('result', params.result);
  if (params.severity) sp.set('severity', params.severity);
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  if (params.traceId) sp.set('traceId', params.traceId);
  if (params.keyword) sp.set('keyword', params.keyword);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.size !== undefined) sp.set('size', String(params.size));
  if (params.sort) sp.set('sort', params.sort);
  return sp;
}

export const auditLogApi = {
  /** GET /api/eraf/audit-logs — 전체 감사 로그 목록 (ROLE_ADMIN) */
  list: (params: AuditLogSearchCondition): Promise<PageResponse<AuditLogListItem>> => {
    const sp = buildSearchParams(params);
    return apiClient
      .get<unknown, { data: PageResponse<AuditLogListItem> }>(`/audit-logs?${sp.toString()}`)
      .then((res) => res.data);
  },

  /** GET /api/eraf/audit-logs/{id} — 감사 로그 상세 */
  get: (id: number): Promise<AuditLogDetailResponse> =>
    apiClient
      .get<unknown, { data: AuditLogDetailResponse }>(`/audit-logs/${id}`)
      .then((res) => res.data),

  /** GET /api/eraf/audit-logs/me — 본인 활동 기록 (AUTHENTICATED) */
  me: (params: Omit<AuditLogSearchCondition, 'actor' | 'result' | 'severity' | 'resourceId' | 'traceId' | 'keyword'>): Promise<PageResponse<AuditLogListItem>> => {
    const sp = new URLSearchParams();
    if (params.action) sp.set('action', params.action);
    if (params.resourceType) sp.set('resourceType', params.resourceType);
    if (params.from) sp.set('from', params.from);
    if (params.to) sp.set('to', params.to);
    if (params.page !== undefined) sp.set('page', String(params.page));
    if (params.size !== undefined) sp.set('size', String(params.size));
    if (params.sort) sp.set('sort', params.sort);
    return apiClient
      .get<unknown, { data: PageResponse<AuditLogListItem> }>(`/audit-logs/me?${sp.toString()}`)
      .then((res) => res.data);
  },

  /** GET /api/eraf/audit-logs/export — 엑셀 다운로드 (ROLE_ADMIN) */
  export: (params: AuditLogSearchCondition, locale: 'ko' | 'en' = 'ko'): Promise<Blob> => {
    const sp = buildSearchParams(params);
    sp.set('locale', locale);
    const token = getStoredToken();
    return axios
      .get(`${import.meta.env.VITE_API_BASE ?? '/api/eraf'}/audit-logs/export?${sp.toString()}`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => res.data as Blob);
  },

  /** GET /api/eraf/audit-logs/actions — 액션 카탈로그 */
  actions: (): Promise<ActionCatalogItem[]> =>
    apiClient
      .get<unknown, { data: ActionCatalogItem[] }>('/audit-logs/actions')
      .then((res) => res.data),

  /** GET /api/eraf/audit-logs/resources — 자원 카탈로그 */
  resources: (): Promise<ResourceCatalogItem[]> =>
    apiClient
      .get<unknown, { data: ResourceCatalogItem[] }>('/audit-logs/resources')
      .then((res) => res.data),

  /** DELETE /api/eraf/audit-logs/all — 전체 삭제 (AUDIT_DELETE_ALL) */
  purgeAll: (): Promise<PurgeResponse> =>
    apiClient
      .delete<unknown, { data: PurgeResponse }>('/audit-logs/all')
      .then((res) => res.data),

  /** POST /api/eraf/audit-logs/bulk-delete — 다중 행 삭제 (#453 · AUDIT_DELETE) */
  bulkDelete: (ids: number[]): Promise<{ deletedCount: number }> =>
    apiClient
      .post<unknown, { data: { deletedCount: number } }>('/audit-logs/bulk-delete', { ids })
      .then((res) => res.data),

  /** DELETE /api/eraf/audit-logs/{id} — 단건 삭제 (#454 · AUDIT_DELETE) */
  deleteOne: (id: number): Promise<{ deletedCount: number }> =>
    apiClient
      .delete<unknown, { data: { deletedCount: number } }>(`/audit-logs/${id}`)
      .then((res) => res.data),
};
